import {params} from '../../vars/params';
import {config} from '../../../config';
import {gsap} from 'gsap';
import {ScrollTrigger} from 'gsap/ScrollTrigger';
import {SplitText} from 'gsap/SplitText';

gsap.registerPlugin(SplitText);
gsap.registerPlugin(ScrollTrigger);

export const scrollIn = (): void => {
  const triggers = document.querySelectorAll('[data-anim-trigger]');
  const anims = document.querySelectorAll('[data-anim]');
  const titles = document.querySelectorAll('[data-anim-title]');
  const zooms = document.querySelectorAll('[data-zoom]');
  const fades = document.querySelectorAll('[data-fade]');
  const typeIns = document.querySelectorAll('[data-type-in]');
  const cards = document.querySelectorAll('[data-card]');
  const ctas = document.querySelectorAll('.c-cta');
  const isSp = window.innerWidth < config.breakPoint;
  const endStart = isSp ? 'top 120px' : 'bottom 40%';
  const endEnd = isSp ? 'top 60px' : 'bottom 20%';
  triggers.forEach((trigger) => {
    const staggers = trigger.querySelectorAll('[data-stagger]');
    gsap.to(staggers, {
      opacity: 1,
      y: 0,
      duration: 0.4,
      stagger: 0.15,
      scrollTrigger: {
        trigger: trigger,
        start: `top ${params.isSp ? config.triggerPos.sp : config.triggerPos.pc}`,
        toggleClass: {targets: staggers, className: '--scrolled'},
        toggleActions: 'play none none reverse',
      },
    });
  });
  anims.forEach((anim) => {
    gsap.to(anim, {
      opacity: 1,
      y: 0,
      duration: 0.4,
      stagger: 0.1,
      scrollTrigger: {
        trigger: anim,
        start: `top ${params.isSp ? config.triggerPos.sp : config.triggerPos.pc}`,
        toggleActions: 'play none none reverse',
      },
    });
  });
  titles.forEach((title) => {
    const en = title.querySelector('.c-ttl-sec__inner') as HTMLElement;
    const ja = title.querySelector('.c-ttl-sec__ja') as HTMLElement;
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: title,
        start: `top ${params.isSp ? config.triggerPos.sp : config.triggerPos.pc}`,
        toggleActions: 'play none none reverse',
      },
    });
    tl.to(en, {
      opacity: 1,
      duration: 1.5,
    });
    tl.to(
      ja,
      {
        opacity: 1,
        duration: 0.5,
      },
      '<0.4'
    );
  });
  zooms.forEach((zoom) => {
    const zoomElm = zoom as HTMLElement;
    const zoomType = zoomElm.dataset.zoom;
    if (zoomType === 'in-out' || zoomType === 'in') {
      const startTl = gsap.timeline({
        scrollTrigger: {
          trigger: zoomElm,
          start: 'top 90%',
          end: 'top 80%',
          scrub: true,
        },
      });
      startTl
        .to(zoomElm, {
          opacity: 1,
          duration: 1,
        })
        .fromTo(
          zoomElm,
          {
            scale: 0.5,
          },
          {
            scale: 1,
            ease: 'power2.out',
            duration: 2,
          },
          0
        );
    }
    if (zoomType === 'in-out' || zoomType === 'out') {
      const endTl = gsap.timeline({
        scrollTrigger: {
          trigger: zoomElm,
          start: endStart,
          end: endEnd,
          scrub: true,
        },
      });
      endTl
        .to(
          zoomElm,
          {
            opacity: 0,
            duration: 1,
          },
          1
        )
        .fromTo(
          zoomElm,
          {
            scale: 1,
          },
          {
            scale: 0.5,
            ease: 'power2.in',
            duration: 2,
          },
          0
        );
    }
  });
  fades.forEach((fade) => {
    const fadeElm = fade as HTMLElement;
    const fadeType = fadeElm.dataset.fade;
    if (fadeType === 'in') {
      gsap.to(fadeElm, {
        opacity: 1,
        duration: 1,
        scrollTrigger: {
          trigger: fadeElm,
          start: 'top 90%',
          toggleActions: 'play none none reverse',
        },
      });
    }
  });
  cards.forEach((card) => {
    const cardElm = card as HTMLElement;
    const cardType = cardElm.dataset.card;
    const cardItems = cardElm.querySelectorAll('[data-card-item]');
    if (window.innerWidth >= config.breakPoint) {
      if (cardType === 'in-out' || cardType === 'in') {
        const startTl = gsap.timeline({
          scrollTrigger: {
            trigger: cardElm,
            start: 'top 90%',
            end: 'bottom 110%',
            scrub: true,
          },
        });
        startTl
          .to(cardItems, {
            opacity: 1,
            duration: 2,
            stagger: 0.2,
          })
          .fromTo(
            cardItems,
            {
              scale: 0.7,
              y: 20,
            },
            {
              scale: 1,
              y: 0,
              ease: 'power1.inOut',
              stagger: 0.2,
              duration: 2,
            },
            0
          );
      }
      if (cardType === 'in-out' || cardType === 'out') {
        const endTl = gsap.timeline({
          scrollTrigger: {
            trigger: cardElm,
            start: 'top 10%',
            end: 'bottom 0%',
            scrub: true,
          },
        });
        endTl
          .to(cardItems, {
            opacity: 0,
            duration: 2,
            stagger: 0.2,
          })
          .fromTo(
            cardItems,
            {
              scale: 1,
              y: 0,
            },
            {
              scale: 0.7,
              y: 20,
              ease: 'power1.inOut',
              stagger: 0.2,
              duration: 2,
            },
            0
          );
      }
    } else {
      cardItems.forEach((item) => {
        if (cardType === 'in-out' || cardType === 'in') {
          const startTl = gsap.timeline({
            scrollTrigger: {
              trigger: item,
              start: 'top 90%',
              end: 'top 70%',
              scrub: true,
            },
          });
          startTl
            .to(item, {
              opacity: 1,
              duration: 2,
            })
            .fromTo(
              item,
              {
                scale: 0.7,
                y: 20,
              },
              {
                scale: 1,
                y: 0,
                ease: 'power1.inOut',
                duration: 2,
              },
              0
            );
        }
        if (cardType === 'in-out' || cardType === 'out') {
          const endTl = gsap.timeline({
            scrollTrigger: {
              trigger: item,
              start: endStart,
              end: endEnd,
              scrub: true,
            },
          });
          endTl
            .to(item, {
              opacity: 0,
              duration: 2,
            })
            .fromTo(
              item,
              {
                scale: 1,
                y: 0,
              },
              {
                scale: 0.7,
                y: 20,
                ease: 'power1.inOut',
                duration: 2,
              },
              0
            );
        }
      });
    }
  });
  typeIns.forEach((typeIn) => {
    const typeInElm = typeIn as HTMLElement;
    const splitText = new SplitText(typeIn, {types: 'chars', tag: 'span'});
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: typeInElm,
        start: 'top 90%',
        end: 'top 80%',
        scrub: true,
      },
    });
    tl.from(splitText.chars, {
      opacity: 0,
      duration: 0.001,
      stagger: 0.05,
    });
  });
  ctas.forEach((cta) => {
    const ctaElm = cta as HTMLElement;
    const origAngle = 122;
    const delta = 200;
    gsap.fromTo(
      ctaElm,
      {
        background: `linear-gradient(${origAngle - delta}deg, #1bb7a5 15%, #f000c6 90%)`,
        scale: 0.8,
      },
      {
        background: `linear-gradient(${origAngle + delta}deg, #1bb7a5 15%, #f000c6 90%)`,
        scale: 1,
        duration: 2,
        ease: 'none',
        scrollTrigger: {
          trigger: ctaElm,
          start: 'top 100%',
          end: 'center 50%',
          scrub: true,
        },
      }
    );
  });
};
